const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.API_URL || 'http://localhost:8080/api';
import { notifyUnauthorized } from './authEvents';
import { buildQueryString } from '../utils/apiHelpers';
import { BaseApiClient, HttpError } from './apiBase';
export { HttpError };
export class ApiClient extends BaseApiClient {
    constructor(baseURL = API_BASE_URL) {
        super(baseURL);
    }
    setBaseUrl(url) {
        this.baseURL = url;
    }
    async makeRequest(endpoint, options = {}) {
        const requestStart = performance.now();
        try {
            // If endpoint is absolute URL, use it as-is; otherwise prepend baseURL
            const isAbsoluteUrl = endpoint.startsWith('http://') || endpoint.startsWith('https://');
            const url = isAbsoluteUrl ? endpoint : `${this.baseURL}${endpoint}`;
            // Don't set Content-Type for FormData - browser will set it automatically with boundary
            const isFormData = options.body instanceof FormData;
            const defaultHeaders = {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            };
            // W3C Trace Context propagation — generate a traceparent header so
            // backend spans are linked to the frontend request.
            try {
                const traceId = Array.from(crypto.getRandomValues(new Uint8Array(16)))
                    .map((b) => b.toString(16).padStart(2, '0'))
                    .join('');
                const spanId = Array.from(crypto.getRandomValues(new Uint8Array(8)))
                    .map((b) => b.toString(16).padStart(2, '0'))
                    .join('');
                defaultHeaders['traceparent'] = `00-${traceId}-${spanId}-01`;
            }
            catch {
                // crypto.getRandomValues not available (e.g. non-browser env) — skip
            }
            // Only add Content-Type for non-FormData requests
            if (!isFormData) {
                defaultHeaders['Content-Type'] = 'application/json';
            }
            const defaultOptions = {
                headers: defaultHeaders,
                credentials: 'include',
            };
            // Best-effort CSRF token support (double-submit cookie pattern)
            const method = (options.method || 'GET').toUpperCase();
            let csrfHeader = {};
            if (method !== 'GET' && typeof document !== 'undefined' && typeof document.cookie === 'string') {
                try {
                    const raw = document.cookie || '';
                    const parts = raw.split(';').map((p) => p.trim());
                    const pair = parts.find((p) => /^((x-)?csrf|xsrf)[-_]?token=/i.test(p));
                    if (pair) {
                        const value = decodeURIComponent(pair.split('=')[1] || '');
                        if (value) {
                            csrfHeader = { 'X-CSRF-Token': value, 'X-XSRF-TOKEN': value };
                        }
                    }
                }
                catch { }
            }
            const response = await fetch(url, {
                ...defaultOptions,
                ...options,
                headers: {
                    ...defaultOptions.headers,
                    ...options.headers,
                    ...csrfHeader,
                },
            });
            if (!response.ok) {
                // Read error body once for both detection and return
                const contentType = response.headers.get('content-type') || '';
                let errorText = '';
                let errorBody;
                try {
                    if (contentType.includes('application/json')) {
                        const j = await response.json();
                        errorBody = j;
                        errorText = (j && (j.error || j.message)) || JSON.stringify(j);
                    }
                    else {
                        errorText = await response.text();
                    }
                }
                catch (e) {
                    errorText = `HTTP ${response.status}: ${response.statusText}`;
                }
                const signalUnauthorized = () => {
                    // Trigger global unauthorized handler for auto-logout
                    notifyUnauthorized();
                    if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('auth:unauthorized', {
                            detail: { status: response.status, message: errorText || response.statusText },
                        }));
                    }
                };
                if (response.status === 401)
                    signalUnauthorized();
                // Some backends use 400 for JWT parse/missing errors
                const lower = (errorText || '').toLowerCase();
                if (response.status === 400 &&
                    (lower.includes('missing or malformed jwt') ||
                        (lower.includes('jwt') && (lower.includes('missing') || lower.includes('malformed') || lower.includes('invalid'))))) {
                    signalUnauthorized();
                }
                // Dispatch observability event for error responses
                try {
                    const durationMs = performance.now() - requestStart;
                    if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('api:request-complete', {
                            detail: {
                                method: (options.method || 'GET').toUpperCase(),
                                endpoint,
                                durationMs,
                                status: response.status,
                                error: errorText || `HTTP ${response.status}`,
                            },
                        }));
                    }
                }
                catch { }
                return {
                    success: false,
                    error: errorText || `HTTP ${response.status}: ${response.statusText}`,
                    status: response.status,
                    errorBody,
                };
            }
            // Handle 204 No Content - no body to parse
            if (response.status === 204) {
                return {
                    success: true,
                    data: undefined,
                    status: response.status,
                };
            }
            // Auto-detect response type by Content-Type header
            const contentType = response.headers.get('content-type') || '';
            let data;
            let cleanup;
            if (contentType.includes('text/csv') ||
                contentType.includes('application/vnd.ms-excel') ||
                contentType.includes('application/vnd.openxmlformats') ||
                contentType.includes('application/octet-stream') ||
                contentType.includes('application/pdf')) {
                data = await response.blob();
            }
            else if (contentType.includes('text/html')) {
                // Handle HTML responses (like performance reports)
                data = await response.text();
            }
            else if (contentType.includes('image/')) {
                let blob;
                if (contentType.includes('svg')) {
                    const svgText = await response.text();
                    blob = new Blob([svgText], { type: 'image/svg+xml' });
                }
                else {
                    blob = await response.blob();
                }
                const objectURL = URL.createObjectURL(blob);
                data = objectURL;
                cleanup = () => URL.revokeObjectURL(objectURL);
            }
            else {
                data = await response.json();
            }
            // Dispatch observability event for API call tracking
            try {
                const durationMs = performance.now() - requestStart;
                if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('api:request-complete', {
                        detail: { method: (options.method || 'GET').toUpperCase(), endpoint, durationMs, status: response.status, error: undefined },
                    }));
                }
            }
            catch { }
            return {
                success: true,
                data: data,
                status: response.status,
                cleanup,
            };
        }
        catch (error) {
            // Dispatch observability event for failed API calls
            try {
                const durationMs = performance.now() - requestStart;
                if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('api:request-complete', {
                        detail: {
                            method: (options.method || 'GET').toUpperCase(),
                            endpoint,
                            durationMs,
                            status: null,
                            error: error instanceof Error ? error.message : 'Network error',
                        },
                    }));
                }
            }
            catch { }
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Network error',
            };
        }
    }
    async get(endpoint, params) {
        let url = endpoint;
        if (params && Object.keys(params).length > 0) {
            const queryString = buildQueryString(params);
            if (queryString) {
                url = `${endpoint}${endpoint.includes('?') ? '&' : '?'}${queryString}`;
            }
        }
        return this.makeRequest(url, { method: 'GET' });
    }
    async post(endpoint, data) {
        return this.makeRequest(endpoint, {
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    async put(endpoint, data) {
        return this.makeRequest(endpoint, {
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    async patch(endpoint, data) {
        return this.makeRequest(endpoint, {
            method: 'PATCH',
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    async delete(endpoint, params) {
        let url = endpoint;
        if (params && Object.keys(params).length > 0) {
            const queryString = buildQueryString(params);
            if (queryString) {
                url = `${endpoint}${endpoint.includes('?') ? '&' : '?'}${queryString}`;
            }
        }
        return this.makeRequest(url, { method: 'DELETE' });
    }
    async uploadFile(endpoint, file, filename) {
        const formData = new FormData();
        // Add filename for Blob to ensure proper content-type and upload handling
        if (filename && file instanceof Blob) {
            formData.append('file', file, filename);
        }
        else {
            formData.append('file', file);
        }
        const result = await this.makeRequest(endpoint, {
            method: 'POST',
            body: formData,
            headers: {},
        });
        return result;
    }
    /**
     * Upload multipart form data (for complex uploads with multiple fields)
     */
    async uploadMultipart(endpoint, formData, signal, headers) {
        return this.makeRequest(endpoint, {
            method: 'POST',
            body: formData,
            headers: headers ?? {}, // Let browser set Content-Type with boundary
            signal,
        });
    }
    /**
     * Get the base URL for the API client
     */
    getBaseUrl() {
        return this.baseURL;
    }
    async downloadFile(url, customHeaders) {
        // For downloadFile, we need the actual Blob, not an object URL
        // So we'll use fetch directly to get the blob
        try {
            const fullUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `${this.baseURL}${url}`;
            const headers = customHeaders || {};
            const response = await fetch(fullUrl, {
                method: 'GET',
                credentials: customHeaders ? undefined : 'include', // Use credentials only if no custom headers
                headers,
            });
            if (!response.ok) {
                throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
            }
            return await response.blob();
        }
        catch (error) {
            console.error('[ApiClient] 📥 downloadFile error:', error);
            throw error;
        }
    }
}
export const apiClient = new ApiClient();
